import {useWindowSize} from '@wandb/weave/common/hooks/useWindowSize';
import {makePropsAreEqual} from '@wandb/weave/common/util/shouldUpdate';
import _ from 'lodash';
import React, {memo, useEffect, useMemo, useState} from 'react';

import {TABLE_MIN_COLUMN_WIDTH} from '../../../util/constants';
import * as TableCols from '../../../util/tablecols';
import * as WBTreeHelpers from '../../../util/wbtree';
import {PROJECT_BAR_OFFSET} from '../../MultiRunWorkspace/constants';
import {
  WBTableColumn,
  WBTableGridContainerProps,
  WBTableHoverCellCoords,
} from './types';
import {WBTableGrid} from './WBTableGrid';

const WBTableGridContainerComp: React.FC<WBTableGridContainerProps> = ({
  addFilter,
  addGroup,
  childrenPaginationMap,
  columnDragAccessor,
  columnDropAccessor,
  columnKeySupportsAggregates,
  columnResizingAccessor,
  columns,
  expandable,
  expanded,
  expandedRowAddresses,
  fixedColumns,
  hideColumn,
  isSingleMode,
  isResizingFullHeight,
  loading,
  moveColumn,
  openSortPopup,
  pinnedTreeRef,
  readOnly,
  replaceColumnAggregation,
  rows,
  scrollXBounds,
  setChildrenPagination,
  setTableSettings,
  setTableState,
  showArtifactCounts,
  showLogCounts,
  sort,
  tableSettings,
  toggleExpandedRow,
  togglePinnedColumn,
  topLevelQueryVariables,
  truncationType,
  unpinnedTreeRef,
  updateSort,
  updateTruncationType,
}: WBTableGridContainerProps) => {
  const windowSize = useWindowSize();
  const [hoverCellCoords, setHoverCellCoords] =
    useState<WBTableHoverCellCoords>([]);

  const pinnedColumns = useMemo(
    () => columns.filter(c => TableCols.isPinned(tableSettings, c.accessor)),
    [tableSettings, columns]
  );

  const unPinnedColumns = useMemo(
    () => columns.filter(c => !TableCols.isPinned(tableSettings, c.accessor)),
    [tableSettings, columns]
  );

  const pinnedColumnsWidth = _.sum(
    pinnedColumns.map(c => WBTreeHelpers.getColumnWidth(tableSettings, c.key))
  );

  // FIXME: the following code shouldn't happen as an effect of the render cycle
  //        instead, there should be a "reconcile changes to the table settings"
  //        function that, given a list of pinned columns, and a max width,
  //        figures out how to resize them if needed. That should then get
  //        called whenever pinned columns change, the window resizes, or the
  //        user resizes the run selector sidebar.

  // allow pinned columns to take up to 70% of available screen space.
  // if exceeded, automatically scale all pinned columns down to fit.
  // to prevent unnecessary re-renders, make sure to work with whole numbers.
  const pinnedColumnsWidthLimit = Math.floor(windowSize.width * 0.7);
  const pinnedColumnsMinWidth = pinnedColumns.length * TABLE_MIN_COLUMN_WIDTH;

  useEffect(() => {
    if (isResizingFullHeight) {
      return;
    }
    if (
      pinnedColumnsWidth > pinnedColumnsWidthLimit &&
      pinnedColumnsWidthLimit > pinnedColumnsMinWidth
    ) {
      const reducedColumnWidths = {...tableSettings.columnWidths};
      pinnedColumns.forEach(column => {
        const curColumnWidth = WBTreeHelpers.getColumnWidth(
          tableSettings,
          column.key
        );
        const percentage = curColumnWidth / pinnedColumnsWidth;
        reducedColumnWidths[column.accessor] = Math.floor(
          Math.max(pinnedColumnsWidthLimit * percentage, TABLE_MIN_COLUMN_WIDTH)
        );
      });
      setTableSettings({
        columnWidths: reducedColumnWidths,
      });
    }
  }, [
    isResizingFullHeight,
    pinnedColumns,
    pinnedColumnsMinWidth,
    pinnedColumnsWidth,
    pinnedColumnsWidthLimit,
    tableSettings,
    setTableSettings,
  ]);

  // Create a wrapper function so we can quickly render a pinned and unpinned table
  const renderTableBody = (opts: {
    cols: WBTableColumn[];
    pinned: boolean;
    leftMargin?: number;
    shouldSkipChildQuery?: boolean;
  }) => {
    return (
      <WBTableGrid
        addFilter={addFilter}
        addGroup={addGroup}
        childrenPaginationMap={childrenPaginationMap}
        columnDragAccessor={columnDragAccessor}
        columnDropAccessor={columnDropAccessor}
        columnKeySupportsAggregates={columnKeySupportsAggregates}
        columnResizingAccessor={columnResizingAccessor}
        columns={opts.cols}
        expandable={expandable}
        expanded={expanded}
        expandedRowAddresses={expandedRowAddresses}
        fixedColumns={fixedColumns}
        hideColumn={hideColumn}
        hoverCellCoords={hoverCellCoords}
        shouldSkipChildQuery={opts.shouldSkipChildQuery}
        isSingleMode={isSingleMode}
        leftMargin={opts.leftMargin || 0}
        loadingTable={loading}
        moveColumn={moveColumn}
        openSortPopup={openSortPopup}
        pinned={opts.pinned}
        readOnly={readOnly}
        replaceColumnAggregation={replaceColumnAggregation}
        rows={rows}
        scrollXBounds={opts.pinned ? [0, 10000] : scrollXBounds}
        setChildrenPagination={setChildrenPagination}
        setHoverCellCoords={setHoverCellCoords}
        setTableSettings={setTableSettings}
        setTableState={setTableState}
        showArtifactCounts={showArtifactCounts}
        showLogCounts={showLogCounts}
        sort={sort}
        tableSettings={tableSettings}
        toggleExpandedRow={toggleExpandedRow}
        togglePinnedColumn={togglePinnedColumn}
        topLevelQueryVariables={topLevelQueryVariables}
        treeRef={opts.pinned ? pinnedTreeRef : unpinnedTreeRef}
        truncationType={truncationType}
        updateSort={updateSort}
        updateTruncationType={updateTruncationType}
      />
    );
  };

  // The pinned columns are too wide if there isn't enough space to render at
  // least 4 minimum-sized columns in the unpinned section of the table.
  const pinnedColumnsAreTooWide =
    pinnedColumnsWidth >
    windowSize.width - (TABLE_MIN_COLUMN_WIDTH * 4 + PROJECT_BAR_OFFSET);

  // We render the table without the pinned columns behavior if:
  // - the screen size is below the tablet breakpoint
  // - the user has pinned all the visible columns
  // - the pinned columns are too wide (see above)
  //
  // In either of those cases, render the table with normal inline scroll.
  if (
    windowSize.responsiveSize === 'small' ||
    unPinnedColumns.length === 0 ||
    pinnedColumnsAreTooWide
  ) {
    return renderTableBody({
      cols: columns,
      pinned: false,
    });
  } else {
    // Otherwise render the table with pinned columns on top
    return (
      <>
        {renderTableBody({
          cols: pinnedColumns,
          pinned: true,
        })}
        {(expandable == null || expanded) &&
          renderTableBody({
            cols: unPinnedColumns,
            pinned: false,
            leftMargin: pinnedColumnsWidth,
            shouldSkipChildQuery: true,
          })}
      </>
    );
  }
};

export const WBTableGridContainer = memo(
  WBTableGridContainerComp,
  makePropsAreEqual({
    name: 'WBTableGridContainerMemo',
    deep: [
      'query',
      'scrollXBounds',
      'groupSelection',
      'groupTempSelection',
      'tempSelectedCount',
      'fixedColumns',
      'columns',
      'topLevelQueryVariables',
      'tableSettings',
      'sort',
    ],
    ignoreFunctions: true,
  })
);
